import { create } from 'zustand';
import * as api from '../lib/api';
import { setUnauthenticatedHandler } from '../lib/api';

/**
 * useAuthStore — who is signed in, and what they are allowed to see.
 *
 * The session itself lives in an httpOnly cookie the JavaScript cannot read,
 * so this store holds no token. It holds the *answer* the server gave to
 * "who am I", refreshed by calling /auth/me. That means a tampered store can
 * change what the UI renders but never what the API permits.
 *
 * `status` starts at 'unknown' rather than 'signed_out' so route guards can
 * wait for the first /auth/me instead of bouncing a signed-in user to /login
 * on every hard refresh.
 */
let bootstrapInFlight = null;

export const useAuthStore = create((set, get) => ({
  status: 'unknown', // unknown | authenticating | signed_in | signed_out
  // True when the session ended under us, as opposed to a deliberate sign-out.
  // Lets the login screen explain why the user is suddenly looking at it.
  expired: false,
  user: null,
  permissions: null,
  error: null,

  /**
   * Called once on app boot, and after any auth mutation.
   *
   * Concurrent callers are coalesced onto one in-flight request. An earlier
   * version guarded by checking `status === 'authenticating'` and returning,
   * which silently dropped the call login() makes right after signing in —
   * leaving the store stuck in 'authenticating' forever and every route guard
   * rendering its spinner with nothing behind it.
   */
  bootstrap: async () => {
    if (bootstrapInFlight) return bootstrapInFlight;

    set({ status: 'authenticating', error: null });
    bootstrapInFlight = (async () => {
      try {
        const { user, permissions } = await api.me();
        set({ status: 'signed_in', user, permissions, error: null });
      } catch {
        // A 401 here is the normal "not signed in" case, not an error worth showing.
        set({ status: 'signed_out', user: null, permissions: null });
      }
    })();

    try {
      return await bootstrapInFlight;
    } finally {
      bootstrapInFlight = null;
    }
  },

  login: async (email, password) => {
    set({ error: null });
    try {
      await api.login(email, password);
      // Re-read from /auth/me rather than trusting the login response, so the
      // permissions object has exactly one source.
      await get().bootstrap();
      return { ok: true };
    } catch (err) {
      set({ status: 'signed_out', user: null, permissions: null, error: err.message });
      return { ok: false, code: err.code, message: err.message };
    }
  },

  register: async (payload) => {
    set({ error: null });
    try {
      await api.register(payload);
      await get().bootstrap();
      return { ok: true };
    } catch (err) {
      set({ status: 'signed_out', error: err.message });
      return { ok: false, code: err.code, message: err.message, details: err.details };
    }
  },

  logout: async () => {
    try {
      await api.logout();
    } finally {
      set({ status: 'signed_out', user: null, permissions: null, error: null, expired: false });
    }
  },

  clearError: () => set({ error: null }),

  /**
   * Called from the API layer when a request comes back unauthenticated.
   *
   * Only meaningful if we thought we were signed in — a 401 while already
   * signed out is just the normal state, and flipping to 'expired' would show
   * an "your session ended" banner to someone who never had one.
   */
  sessionExpired: () => {
    if (get().status !== 'signed_in') return;
    set({ status: 'signed_out', user: null, permissions: null, expired: true });
  },

  clearExpired: () => set({ expired: false }),

  // -- derived -------------------------------------------------------------
  is: (role) => get().user?.role === role,
  isStaff: () => ['attendant', 'admin', 'super_admin', 'platform_admin'].includes(get().user?.role),
  can: (permission) => Boolean(get().permissions?.[permission]),
}));

/** Rank-based check mirroring the server's middleware/auth.js. */
/**
 * Mirrors the server's table in middleware/auth.js. The two must agree: a role
 * missing here reads as rank -1 and the guard locks that user out of their own
 * console, while the API happily serves them.
 */
const RANK = { customer: 0, attendant: 10, admin: 20, super_admin: 30, platform_admin: 40 };
export const hasRole = (userRole, minimumRole) =>
  (RANK[userRole] ?? -1) >= (RANK[minimumRole] ?? Infinity);

/** Where each role lands after signing in. */
export const homePathFor = (role) =>
  ({ customer: '/account', attendant: '/gate', admin: '/admin', super_admin: '/admin',
     platform_admin: '/platform' })[role] ?? '/';

export const useUser = () => useAuthStore((s) => s.user);
export const useAuthStatus = () => useAuthStore((s) => s.status);


// Wire the API layer's 401 hook to the store. Done here rather than in a
// component so it is registered before the first request can fire.
setUnauthenticatedHandler(() => useAuthStore.getState().sessionExpired());
